import React, {useState, useEffect} from "react";
import {useSubflows} from "@/contexts/SubflowsContext";
import {Button} from "@/components/ui/Button";
import {Input} from "@/components/ui/input";
import {Tabs, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import {
  Trash2,
  Copy,
  Edit,
  Plus,
  MoreHorizontal,
  WorkflowIcon,
} from "lucide-react";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {getAssistantDetails} from "@/api/services/queries";
import {Assistant} from "@/api/services";
import {AssistantType, ProcessedNode, FlowNodeData} from "@/types/node";
import {Node, Edge} from "@xyflow/react";
import {
  applyFlowMigration,
  LATEST_FLOW_DATA_VERSION,
} from "@/lib/flowMigrations";
import {restoreNodeSettings, serializeFlowData} from "@/utils/index";
import {
  createMainFlowDefaultNodes,
  createMainFlowDefaultEdges,
  createDefaultNodes,
  createDefaultEdges,
} from "@/utils/defaultFlowData";
import {FlowMode, SavingState} from "@/types/node";
import {useSave} from "@/contexts/SaveContext";
import {useVariables} from "@/contexts/VariablesContext";
import {Workflow} from "../icons";
import {findAllSubflowsIssues} from "@/lib/utils";
import {remoteValidateFsmCollectIssues} from "@/lib/utils";
import {convertToFSM} from "@/lib/fsmConverter";

const SubflowManager: React.FC = () => {
  const {
    subflows,
    selectedSubflowId,
    addSubflow,
    updateSubflow,
    removeSubflow,
    duplicateSubflow,
    selectSubflow,
  } = useSubflows();

  const {
    setSavingState,
    setInitialFlowData,
    setFlowMode,
    handleSave,
    setPermanentBlockSave,
  } = useSave();
  const {syncVariablesWithNodes} = useVariables();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const {setFlowIssues} = useSave();

  // Ensure main flow is selected by default on mount
  useEffect(() => {
    if (selectedSubflowId === null) {
      selectSubflow("main"); // "main" is the ID for the main flow
    }
  }, [selectedSubflowId, selectSubflow]);

  // Flow loading logic moved from Canvas
  useEffect(() => {
    const fetchFlow = (attempt = 1) => {
      getAssistantDetails(FlowBuilderSettings.modelId)
        .then(
          async (
            data: Assistant & {
              json_flow_designer: string;
              agent_type: AssistantType;
            },
          ) => {
            const parsedData = data.json_flow_designer
              ? JSON.parse(data.json_flow_designer)
              : null;

            // Use main flow default nodes (with global and start nodes)
            const mainFlowDefaultNodes = createMainFlowDefaultNodes();
            const mainFlowDefaultEdges =
              createMainFlowDefaultEdges(mainFlowDefaultNodes);

            if (!parsedData) {
              const fallbackNodes = mainFlowDefaultNodes.map(node => ({
                ...node,
                selected: false,
              }));

              updateSubflow("main", {
                flowData: {
                  live: {
                    version: 0,
                    nodes: fallbackNodes,
                    edges: mainFlowDefaultEdges,
                  },
                  test: {
                    version: 0,
                    nodes: fallbackNodes,
                    edges: mainFlowDefaultEdges,
                  },
                },
              });

              setSavingState(SavingState.SAVED);
              return;
            }

            // Check if it's the new format (has mainFlow property) or old format
            const isNewFormat = parsedData.mainFlow !== undefined;

            // Variables for validation (will be set based on format)
            let mainFlowForValidation: any;
            let subflowsForValidation: any[];
            let processedNodesForValidation: Node<FlowNodeData>[];
            let variablesForValidation: Array<{name: string}>;

            if (isNewFormat) {
              // New format: { mainFlow, subflows, FSM }
              const {mainFlow: mainFlowData, subflows: subflowsData} =
                parsedData;

              // Process main flow - apply migrations
              const mainFlowLive = await applyFlowMigration(
                mainFlowData.live,
                true,
              );
              const mainFlowTest = await applyFlowMigration(
                mainFlowData.test,
                true,
              );

              // Restore node settings for main flow
              const mainFlowLiveNodes = restoreNodeSettings(
                mainFlowLive.nodes,
                mainFlowDefaultNodes,
              );
              const mainFlowTestNodes = restoreNodeSettings(
                mainFlowTest.nodes,
                mainFlowDefaultNodes,
              );

              // Update main flow
              updateSubflow("main", {
                flowData: {
                  live: {
                    version: mainFlowLive.version || LATEST_FLOW_DATA_VERSION,
                    nodes: mainFlowLiveNodes,
                    edges: mainFlowLive.edges || mainFlowDefaultEdges,
                  },
                  test: {
                    version: mainFlowTest.version || LATEST_FLOW_DATA_VERSION,
                    nodes: mainFlowTestNodes,
                    edges: mainFlowTest.edges || mainFlowDefaultEdges,
                  },
                },
              });

              // Process and load all subflows
              const loadedSubflowsTestData: Array<{
                nodes: Node<FlowNodeData>[];
                edges: Edge[];
              }> = [];

              // Collect subflows for validation
              const loadedSubflowsForValidation: Array<{
                id: string;
                name: string;
                flowData: {
                  test: {
                    nodes: Node<FlowNodeData>[];
                    edges: Edge[];
                    version: number;
                  };
                };
              }> = [];

              if (subflowsData && Array.isArray(subflowsData)) {
                for (const subflowData of subflowsData) {
                  // Use default subflow nodes (no global/start nodes)
                  const subflowDefaultNodes = createDefaultNodes();
                  const subflowDefaultEdges =
                    createDefaultEdges(subflowDefaultNodes);

                  // Apply migrations to subflow data
                  const subflowLive = await applyFlowMigration(
                    subflowData.flowData.live,
                    true,
                  );
                  const subflowTest = await applyFlowMigration(
                    subflowData.flowData.test,
                    true,
                  );

                  // Restore node settings for subflow
                  const subflowLiveNodes = restoreNodeSettings(
                    subflowLive.nodes,
                    subflowDefaultNodes,
                  );
                  const subflowTestNodes = restoreNodeSettings(
                    subflowTest.nodes,
                    subflowDefaultNodes,
                  );

                  // Add subflow to context - let addSubflow generate the ID
                  addSubflow({
                    name: subflowData.name,
                    description: subflowData.description,
                    flowData: {
                      live: {
                        version:
                          subflowLive.version || LATEST_FLOW_DATA_VERSION,
                        nodes: subflowLiveNodes,
                        edges: subflowLive.edges || subflowDefaultEdges,
                      },
                      test: {
                        version:
                          subflowTest.version || LATEST_FLOW_DATA_VERSION,
                        nodes: subflowTestNodes,
                        edges: subflowTest.edges || subflowDefaultEdges,
                      },
                    },
                  });

                  // Collect test data for initial state comparison
                  loadedSubflowsTestData.push({
                    nodes: subflowTestNodes,
                    edges: subflowTest.edges || subflowDefaultEdges,
                  });

                  // Collect for validation (use temporary ID as it will be assigned by addSubflow)
                  loadedSubflowsForValidation.push({
                    id:
                      subflowData.id ||
                      `subflow-${loadedSubflowsForValidation.length}`,
                    name: subflowData.name,
                    flowData: {
                      test: {
                        nodes: subflowTestNodes,
                        edges: subflowTest.edges || subflowDefaultEdges,
                        version:
                          subflowTest.version || LATEST_FLOW_DATA_VERSION,
                      },
                    },
                  });
                }
              }

              // Sync variables from main flow test nodes and collect them
              const allSyncedVariables: Array<{name: string}> = [];
              if (mainFlowTestNodes?.length > 0) {
                const mainFlowVars = syncVariablesWithNodes(
                  mainFlowTest.nodes,
                  "main",
                  "Main",
                );
                allSyncedVariables.push(...mainFlowVars);
              }

              // Set initial flow data for all flows (no setTimeout needed)
              const allFlowsData = JSON.stringify({
                mainFlow: serializeFlowData(
                  mainFlowTestNodes,
                  mainFlowTest.edges || mainFlowDefaultEdges,
                ),
                subflows: loadedSubflowsTestData.map(sf =>
                  serializeFlowData(sf.nodes, sf.edges),
                ),
              });
              setInitialFlowData(allFlowsData);

              // Set validation data for new format
              mainFlowForValidation = {
                flowData: {
                  test: {
                    nodes: mainFlowTestNodes,
                    edges: mainFlowTest.edges || mainFlowDefaultEdges,
                    version: mainFlowTest.version || LATEST_FLOW_DATA_VERSION,
                  },
                },
              };
              subflowsForValidation = loadedSubflowsForValidation;
              processedNodesForValidation = mainFlowTestNodes;
              variablesForValidation = allSyncedVariables;
            } else {
              // Old format: backwards compatibility
              const flowData = await applyFlowMigration(parsedData);

              const fetchedNodes = restoreNodeSettings(
                flowData.nodes,
                mainFlowDefaultNodes,
              );
              const fetchedEdges = flowData?.edges || mainFlowDefaultEdges;

              updateSubflow("main", {
                flowData: {
                  live: {
                    version: flowData?.version || LATEST_FLOW_DATA_VERSION,
                    nodes: fetchedNodes,
                    edges: fetchedEdges,
                  },
                  test: {
                    version: flowData?.version || LATEST_FLOW_DATA_VERSION,
                    nodes: fetchedNodes,
                    edges: fetchedEdges,
                  },
                },
              });

              const allSyncedVariables: Array<{name: string}> = [];
              if (fetchedNodes?.length > 0) {
                const mainFlowVars = syncVariablesWithNodes(
                  flowData.nodes,
                  "main",
                  "Main",
                );
                allSyncedVariables.push(...mainFlowVars);
              }

              // For old format, only main flow exists
              const allFlowsData = JSON.stringify({
                mainFlow: serializeFlowData(fetchedNodes, fetchedEdges),
                subflows: [],
              });
              setInitialFlowData(allFlowsData);

              // Set validation data for old format
              mainFlowForValidation = {
                flowData: {
                  test: {
                    nodes: fetchedNodes,
                    edges: fetchedEdges,
                    version: flowData?.version || LATEST_FLOW_DATA_VERSION,
                  },
                },
              };
              subflowsForValidation = [];
              processedNodesForValidation = fetchedNodes;
              variablesForValidation = allSyncedVariables;
            }

            setSavingState(SavingState.SAVED);

            // Validate using freshly loaded data
            const baseIssues = findAllSubflowsIssues(
              mainFlowForValidation,
              subflowsForValidation,
              variablesForValidation,
            );

            const fsmData = convertToFSM(
              mainFlowForValidation,
              subflowsForValidation,
              syncVariablesWithNodes,
            );

            if (baseIssues.length > 0) {
              setFlowIssues([...baseIssues]);
            } else {
              remoteValidateFsmCollectIssues({
                modelId: FlowBuilderSettings.modelId,
                fsmData: fsmData,
                nodes:
                  processedNodesForValidation as unknown as Node<FlowNodeData>[],
              }).then(res => {
                setFlowIssues([...res]);
              });
            }
          },
        )
        .catch(error => {
          console.error(`Error fetching stages (attempt ${attempt}):`, error);
          const maxAttempts = 10;

          if (attempt < maxAttempts) {
            console.log("Retrying fetch...");
            fetchFlow(attempt + 1);
            return;
          }

          // PERMANENTLY block saves to prevent data loss if network comes back
          // This block will only be removed by page refresh, which will retry loading
          // Use setPermanentBlockSave so other components can't reset it
          setPermanentBlockSave(true);
          setSavingState(SavingState.ERROR_LOADING);
        });
    };

    if (!FlowBuilderSettings.demoMode) {
      fetchFlow();
    } else {
      updateSubflow("main", {
        flowData: {
          live: {
            version: 0,
            nodes: FlowBuilderSettings.demoData.nodes,
            edges: FlowBuilderSettings.demoData.edges,
          },
          test: {
            version: 0,
            nodes: FlowBuilderSettings.demoData.nodes,
            edges: FlowBuilderSettings.demoData.edges,
          },
        },
      });
      setFlowMode(FlowMode.LIVE);
      setSavingState(SavingState.SAVED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNew = () => {
    const newSubflow = addSubflow({
      name: "",
      description: "",
    });
    setEditingId(newSubflow.id);
    setEditingName("");
    // Trigger save after adding subflow (SaveContext uses refs for latest data)
    handleSave();
  };

  const handleRename = (id: string) => {
    const subflow = subflows.find(s => s.id === id);
    if (subflow) {
      setEditingId(id);
      setEditingName(subflow.name);
    }
  };

  const handleSaveRename = () => {
    if (editingId && editingName.trim()) {
      updateSubflow(editingId, {
        name: editingName.trim(),
      });
      setEditingId(null);
      setEditingName("");
      // Trigger save after renaming subflow (SaveContext uses refs for latest data)
      handleSave();
    }
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveRename();
    } else if (e.key === "Escape") {
      handleCancelRename();
    }
  };

  const handleDuplicate = (id: string) => {
    duplicateSubflow(id);
    // Trigger save after duplicating subflow (SaveContext uses refs for latest data)
    handleSave();
  };

  const handleRemove = (id: string) => {
    removeSubflow(id);
    // Trigger save after removing subflow (SaveContext uses refs for latest data)
    handleSave();
  };

  return (
    <div className="w-full">
      <Tabs
        value={selectedSubflowId || "main"}
        onValueChange={value => {
          selectSubflow(value);
        }}
        className="w-full p-0"
      >
        <TabsList className="p-0 bg-white border-t-0 flex justify-start w-full rounded-none overflow-x-auto h-[34px]">
          <TabsTrigger
            value="main"
            className="relative group flex items-center justify-between gap-2 px-3 h-full text-sm min-w-0 max-w-[120px] transition-all m-0 ease-in-out duration-100 border-transparent border-r border-r-border data-[state=active]:border-b-primary data-[state=active]:border-b-2"
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Workflow className="h-4 w-4 shrink-0" />
              <span className="truncate font-medium text-xs">Main Flow</span>
            </div>
          </TabsTrigger>

          {subflows.map(subflow => (
            <TabsTrigger
              key={subflow.id}
              value={subflow.id}
              className="relative group flex items-center justify-between gap-2 px-3 h-full text-sm min-w-0 max-w-[160px] flex-shrink-0 transition-all m-0 ease-in-out duration-100 border-transparent border-r border-r-border data-[state=active]:border-b-primary data-[state=active]:border-b-2"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <WorkflowIcon className="h-4 w-4 shrink-0" />
                {editingId === subflow.id ? (
                  <Input
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    onBlur={handleSaveRename}
                    onKeyDown={handleKeyPress}
                    className="h-5 px-1 py-0 border-0 bg-transparent text-xs"
                    autoFocus
                    onClick={e => e.stopPropagation()}
                    placeholder="Add Name"
                  />
                ) : (
                  <span className="truncate text-xs">
                    {subflow.name || "Untitled"}
                  </span>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity [&_svg]:size-5"
                    onClick={e => e.stopPropagation()}
                    asDiv
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={e => {
                      e.stopPropagation();
                      handleRename(subflow.id);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={e => {
                      e.stopPropagation();
                      handleDuplicate(subflow.id);
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={e => {
                      e.stopPropagation();
                      handleRemove(subflow.id);
                    }}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TabsTrigger>
          ))}

          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleAddNew}
            className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 flex-shrink-0 [&_svg]:size-4 ml-2"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </TabsList>
      </Tabs>
    </div>
  );
};

export default SubflowManager;
